// Home weather card, same approach as the clock kiosk (src/clockkiosk/src/app.jsx):
// Open-Meteo forecast (no key), placed by the run's warehouse coordinates or,
// failing that, the capital city named by the state tenant.

export const WEATHER_FORECAST_URL = 'https://api.open-meteo.com/v1/forecast';

// AVC states sit in Australian capitals, so a city or state mention in the
// state tenant's name or region is enough to place the forecast.
const AU_CITIES = [
	{ name: 'Sydney', keywords: ['sydney', 'new south wales'], regionCodes: ['nsw'], latitude: -33.8688, longitude: 151.2093 },
	{ name: 'Melbourne', keywords: ['melbourne', 'victoria'], regionCodes: ['vic'], latitude: -37.8136, longitude: 144.9631 },
	{ name: 'Brisbane', keywords: ['brisbane', 'queensland'], regionCodes: ['qld'], latitude: -27.4698, longitude: 153.0251 },
	{ name: 'Perth', keywords: ['perth', 'western australia'], regionCodes: ['wa'], latitude: -31.9523, longitude: 115.8613 },
	{ name: 'Adelaide', keywords: ['adelaide', 'south australia'], regionCodes: ['sa'], latitude: -34.9285, longitude: 138.6007 },
	{ name: 'Hobart', keywords: ['hobart', 'tasmania'], regionCodes: ['tas'], latitude: -42.8821, longitude: 147.3272 },
	{ name: 'Darwin', keywords: ['darwin', 'northern territory'], regionCodes: ['nt'], latitude: -12.4634, longitude: 130.8456 },
	{ name: 'Canberra', keywords: ['canberra', 'australian capital territory'], regionCodes: ['act'], latitude: -35.2809, longitude: 149.13 },
];

const parseCoordinates = (coordinates) => {
	if (!Array.isArray(coordinates) || coordinates.length !== 2) {
		return null;
	}

	// The location type resolves GeoJSON into [latitude, longitude].
	const latitude = Number(coordinates[0]);
	const longitude = Number(coordinates[1]);
	if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || (!latitude && !longitude)) {
		return null;
	}

	return { latitude, longitude };
};

/**
 * Where to forecast: { latitude, longitude, label } or null.
 * - warehouse: the run's start location ({ coordinates, addressObj: { city } })
 * - tenantName, tenantRegion: the state tenant, used when the warehouse has no coordinates
 */
export const resolveWeatherLocation = ({ warehouse, tenantName, tenantRegion }) => {
	const warehouseCoordinates = parseCoordinates(warehouse?.coordinates);
	const searchText = `${warehouse?.addressObj?.city || ''} ${tenantName || ''}`.toLowerCase();
	const regionCode = String(tenantRegion || warehouse?.addressObj?.region || '').trim().toLowerCase();
	const city = AU_CITIES.find(item => (
		item.keywords.some(keyword => searchText.includes(keyword)) ||
		item.regionCodes.includes(regionCode)
	));

	if (warehouseCoordinates) {
		return { ...warehouseCoordinates, label: warehouse?.addressObj?.city || city?.name || null };
	}

	return city ? { latitude: city.latitude, longitude: city.longitude, label: city.name } : null;
};

// null for missing values (Number(null) would read as 0).
const toNumber = value => (value === null || value === undefined || value === '' ? null : Number(value));
const toRoundedNumber = (value) => {
	const number = toNumber(value);
	return Number.isFinite(number) ? Math.round(number) : null;
};

// WMO weather interpretation codes from the Open-Meteo forecast API → label and Feather icon.
export const describeWeatherCode = (weatherCode) => {
	const code = toNumber(weatherCode);
	if (!Number.isFinite(code)) {
		return null;
	}
	if (code === 0) {
		return { label: 'Clear', icon: 'sun' };
	}
	if (code === 1 || code === 2) {
		return { label: 'Partly cloudy', icon: 'cloud' };
	}
	if (code === 45 || code === 48) {
		return { label: 'Fog', icon: 'cloud' };
	}
	if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) {
		return { label: code < 60 ? 'Drizzle' : 'Rain', icon: code < 60 ? 'cloud-drizzle' : 'cloud-rain' };
	}
	if ((code >= 71 && code <= 77) || code === 85 || code === 86) {
		return { label: 'Snow', icon: 'cloud-snow' };
	}
	if (code >= 95 && code <= 99) {
		return { label: 'Storm', icon: 'cloud-lightning' };
	}

	return { label: 'Cloudy', icon: 'cloud' };
};

/** Normalised forecast for the card, or null when the response is unusable. */
export const parseWeatherForecast = (data) => {
	const temperature = toRoundedNumber(data?.current?.temperature_2m);
	const description = describeWeatherCode(data?.current?.weather_code);
	if (temperature === null || !description) {
		return null;
	}

	return {
		temperature,
		...description,
		high: toRoundedNumber(data?.daily?.temperature_2m_max?.[0]),
		low: toRoundedNumber(data?.daily?.temperature_2m_min?.[0]),
		rainChance: toRoundedNumber(data?.daily?.precipitation_probability_max?.[0]),
	};
};
